/**
 * KiaanLockedCard — shown inside the AI panel when AURA's AI endpoints
 * return HTTP 402 with { error: "kiaan_subscription_required", ... }.
 *
 * Design: warm ivory background, saffron accent, lock icon from lucide-react.
 * Light-theme only (founder rule, Apr 17 2026). All colours via design tokens.
 */
import { Lock, ExternalLink, X } from 'lucide-react';

interface KiaanLockedCardProps {
  message: string;
  ctaUrl: string;
  ctaLabel: string;
  onDismiss?: () => void;
}

export function KiaanLockedCard({
  message,
  ctaUrl,
  ctaLabel,
  onDismiss,
}: KiaanLockedCardProps) {
  return (
    <div
      className="relative mb-4 rounded-xl border p-4"
      style={{
        background: 'var(--ivory-0)',
        borderColor: 'var(--saffron-300, #E8C07A)',
      }}
    >
      {/* Dismiss button */}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-3 top-3 flex items-center justify-center rounded"
          style={{
            width: 22,
            height: 22,
            color: 'var(--ink-3)',
            background: 'transparent',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          title="Dismiss"
        >
          <X size={13} />
        </button>
      )}

      {/* Lock icon */}
      <div
        className="mb-3 flex h-10 w-10 items-center justify-center rounded-full"
        style={{
          background: 'linear-gradient(135deg, #C98A3B22, #B87A2E33)',
          border: '1px solid var(--saffron-300, #E8C07A)',
        }}
      >
        <Lock size={18} style={{ color: 'var(--saffron-600, #B87A2E)' }} />
      </div>

      {/* Headline */}
      <div
        className="mb-1 text-[13px] font-semibold"
        style={{ color: 'var(--ink-0)', fontFamily: 'var(--ff-serif, serif)' }}
      >
        Kiaan is locked here
      </div>

      {/* Message from server */}
      <p
        className="mb-3 text-[12px] leading-relaxed"
        style={{ color: 'var(--ink-2)' }}
      >
        {message}
      </p>

      {/* CTA button */}
      <a
        href={ctaUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition-opacity hover:opacity-90"
        style={{
          background: 'linear-gradient(135deg, #C98A3B, #B87A2E)',
          color: '#fff',
          textDecoration: 'none',
        }}
      >
        {ctaLabel}
        <ExternalLink size={11} />
      </a>
    </div>
  );
}
